import { getColor } from 'helpers/utils';
import _ from 'lodash';

const getNumericValues = values => {
  return _.filter(
    _.map(values, value => Number(value)),
    value => Number.isFinite(value)
  );
};

const getNiceInterval = value => {
  if (!value || value <= 0) {
    return 1;
  }

  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  const steps = [1, 2, 2.5, 5, 10];
  const step = _.find(steps, option => normalized <= option) || 10;

  return step * magnitude;
};

const formatCompactAxisNumber = (value, divisor, suffix) =>
  `${(Number(value) / divisor).toLocaleString(undefined, {
    maximumFractionDigits: 2
  })}${suffix}`;

const formatAxisNumber = (value, compactThousands = false) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return `${value}`;
  }

  if (compactThousands && Math.abs(numericValue) >= 1000000) {
    return formatCompactAxisNumber(numericValue, 1000000, 'M');
  }

  if (compactThousands && Math.abs(numericValue) >= 1000) {
    return formatCompactAxisNumber(numericValue, 1000, 'K');
  }

  if (Number.isInteger(numericValue)) {
    return `${numericValue}`;
  }

  return numericValue.toFixed(2).replace(/\.?0+$/, '');
};

export const getDashboardValueAxisBounds = values => {
  const numericValues = getNumericValues(values);
  const maxValue = Math.max(...numericValues, 0);

  if (maxValue <= 0) {
    return {
      max: 1,
      interval: 1
    };
  }

  const integerValues = _.every(numericValues, value =>
    Number.isInteger(value)
  );
  const rawInterval = getNiceInterval(maxValue / 4);
  const interval = integerValues
    ? Math.max(1, Math.ceil(rawInterval))
    : rawInterval;
  const max = Math.max(interval, interval * Math.ceil(maxValue / interval));

  return {
    max,
    interval
  };
};

const getTooltipStyle = () => ({
  trigger: 'axis',
  padding: [7, 10],
  backgroundColor: getColor('gray-100'),
  borderColor: getColor('gray-300'),
  textStyle: { color: getColor('dark') },
  borderWidth: 1,
  transitionDuration: 0
});

const getValueAxisNameGap = compactYAxisLabels =>
  compactYAxisLabels ? 38 : 58;

const getValueAxisLabelMargin = compactYAxisLabels =>
  compactYAxisLabels ? 8 : 12;

const getGridLeft = compactYAxisLabels => (compactYAxisLabels ? 48 : 24);

const getCategoryAxis = (keys, label, labelRotate = 0) => ({
  show: true,
  type: 'category',
  name: label,
  nameLocation: 'middle',
  nameGap: labelRotate ? 72 : 36,
  nameTextStyle: {
    color: getColor('gray-700'),
    fontWeight: 600
  },
  data: keys && keys.length > 0 ? keys : ['No data'],
  boundaryGap: true,
  axisLine: {
    lineStyle: {
      color: getColor('gray-300')
    }
  },
  axisTick: { show: false },
  axisLabel: {
    show: true,
    color: getColor('gray-500'),
    interval: 0,
    hideOverlap: true,
    rotate: labelRotate,
    overflow: 'truncate',
    width: labelRotate ? 90 : 80
  }
});

const getValueAxis = (values, label, valuePrefix, compactYAxisLabels) => {
  const bounds = getDashboardValueAxisBounds(values);

  return {
    show: true,
    type: 'value',
    name: label,
    nameLocation: 'middle',
    nameGap: getValueAxisNameGap(compactYAxisLabels),
    nameTextStyle: {
      color: getColor('gray-700'),
      fontWeight: 600
    },
    min: 0,
    max: bounds.max,
    interval: bounds.interval,
    splitLine: {
      lineStyle: {
        color: getColor('gray-200'),
        type: 'dashed'
      }
    },
    axisPointer: { show: false },
    axisTick: { show: false },
    axisLine: { show: false },
    axisLabel: {
      show: true,
      color: getColor('gray-500'),
      formatter: value =>
        `${valuePrefix || ''}${formatAxisNumber(value, compactYAxisLabels)}`,
      margin: getValueAxisLabelMargin(compactYAxisLabels)
    }
  };
};

export const getDashboardBarOptions = ({
  colors,
  data,
  keys,
  xAxisLabel,
  xAxisLabelRotate,
  yAxisLabel,
  valuePrefix,
  compactYAxisLabels
}) => {
  const values = data && data.length > 0 ? data : [];
  const seriesData =
    colors && colors.length > 0
      ? values.map((value, index) => ({
          value,
          itemStyle: {
            color: colors[index] || getColor('primary')
          }
        }))
      : values;

  return {
    tooltip: {
      ...getTooltipStyle(),
      formatter: params => {
        const item = params[0];
        return `<strong>${item.name}</strong>: ${valuePrefix || ''}${
          item.value
        }`;
      }
    },
    xAxis: getCategoryAxis(keys, xAxisLabel, xAxisLabelRotate),
    yAxis: getValueAxis(values, yAxisLabel, valuePrefix, compactYAxisLabels),
    series: [
      {
        type: 'bar',
        data: seriesData,
        barMaxWidth: 32,
        itemStyle: {
          borderRadius: [4, 4, 0, 0],
          color: getColor('primary')
        }
      }
    ],
    grid: {
      right: 16,
      left: getGridLeft(compactYAxisLabels),
      bottom: xAxisLabelRotate ? 96 : 64,
      top: 24,
      containLabel: true
    }
  };
};
